import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

import { RADIUS } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';
import { hubSearchBoxSx } from 'src/components/cmui/hub/hub-search-box';

import { CM } from '../constants/home-theme';
import { HomeSceneFrame } from './home-scene-frame';
import { HomeSceneMediaGrid } from './home-scene-media-grid';
import { useHomeTyping } from '../hooks/model/use-home-typing';
import { CONTENTS_CARDS, CONTENTS_QUERY } from '../constants/home-scene-samples';

import type { HomeScene } from '../constants/home-copy';

// ----------------------------------------------------------------------

/** 폰에 펴는 카드 — 글 · 광고 · 상품 · 글 넉 장(두 열) */
const PHONE_CARD_KEYS = ['organic:home-rhode-swatch', 'ad:home-rhode-makeup', 'product:home-rhode-tint', 'organic:home-short-rhode-blush'];

/** 공개 홈의 검색칸 — 허브 검색칸과 같은 꼴(`hubSearchBoxSx` · 네모 모서리)을 크게 */
const SEARCH_SX = {
  ...hubSearchBoxSx(),
  width: { xs: 1, sm: 520 },
  minHeight: { xs: 52, md: 56 },
  px: 2,
  gap: 1.5,
  borderColor: CM.primary,
  boxShadow: `0 0 0 3px ${CM.alpha(0.12)}`,
} as const;

// ----------------------------------------------------------------------

/**
 * 장면 1 · 컨텐츠 허브 — 검색칸에 「rhode」가 쳐지고, 소셜 글 · Meta 광고 · 판매몰 상품이 한 격자에 깔린다.
 *
 * 🔑 격자는 실화면 부품 그대로다(`HomeSceneMediaGrid` → `MediaGrid`) · 카드는 전부 정적 섬네일이다.
 * 🔑 장면이 들어오면 검색칸에 검색어가 한 글자씩 쳐지고 커서가 깜박인 뒤 격자에 테가 한 번 돈다 — 한 번만.
 *   격자는 처음부터 보인다(흐리게 기다리지 않는다) · 움직임을 줄이라는 설정이면 검색어가 처음부터 쳐져 있다.
 * 🔑 넓은 화면은 열 장(다섯 장씩 두 줄), 폰은 넉 장(두 열)이다.
 */
export function HomeSceneContents({ scene }: { scene: HomeScene }) {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const cards = phone ? CONTENTS_CARDS.filter((card) => PHONE_CARD_KEYS.includes(card.key)) : CONTENTS_CARDS;

  return (
    <HomeSceneFrame scene={scene} layout="stack">
      {(shown) => <ContentsStage shown={shown} cards={cards} />}
    </HomeSceneFrame>
  );
}

// ----------------------------------------------------------------------

function ContentsStage({ shown, cards }: { shown: boolean; cards: typeof CONTENTS_CARDS }) {
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const { typed, done } = useHomeTyping(CONTENTS_QUERY, shown, reduceMotion);

  return (
    <Stack spacing={2}>
      <Box role="search" aria-label={`검색어 ${CONTENTS_QUERY}`} sx={SEARCH_SX}>
        <Iconify icon="solar:magnifer-linear" width={20} sx={{ color: CM.primary, flexShrink: 0 }} />
        <Typography component="span" sx={{ fontSize: { xs: 18, md: 20 }, fontWeight: 600, color: CM.textDark, display: 'inline-flex', alignItems: 'center' }}>
          {typed}
          {!done && (
            <Box
              component="span"
              aria-hidden
              sx={{
                display: 'inline-block',
                width: 2,
                height: '1.1em',
                ml: 0.25,
                bgcolor: CM.primary,
                animation: 'home-caret 1s steps(1) infinite',
                '@keyframes home-caret': { '50%': { opacity: 0 } },
              }}
            />
          )}
        </Typography>
      </Box>

      {/* 다 쳐지면 격자 둘레에 옅은 테가 한 번 번졌다 사라진다 — 격자 자체는 처음부터 보인다 */}
      <Box
        sx={{
          borderRadius: RADIUS.lg,
          ...(done && !reduceMotion && {
            animation: 'home-grid-ring 1.6s ease-out 1',
            '@keyframes home-grid-ring': {
              '0%': { boxShadow: `0 0 0 0 ${CM.alpha(0)}` },
              '30%': { boxShadow: `0 0 0 6px ${CM.alpha(0.28)}` },
              '100%': { boxShadow: `0 0 0 12px ${CM.alpha(0)}` },
            },
          }),
        }}
      >
        <HomeSceneMediaGrid cards={cards} perRow={5} maxRowHeight={320} />
      </Box>
    </Stack>
  );
}
